import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Search } from 'lucide-react';
import { toast } from 'sonner';

L.Icon.Default.mergeOptions({ iconRetinaUrl: iconRetina, iconUrl, shadowUrl });

const DEFAULT_CENTER = [40.4168, -3.7038]; // Madrid

export const geocodificarDireccion = async (texto) => {
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(texto)}`;
  const res = await fetch(url, { headers: { 'Accept-Language': 'es' } });
  const data = await res.json();
  if (data && data.length) {
    return { lat: parseFloat(data[0].lat).toFixed(6), lng: parseFloat(data[0].lon).toFixed(6) };
  }
  return null;
};

const ClickHandler = ({ onPick }) => {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
};

const Recenter = ({ lat, lng }) => {
  const map = useMap();
  React.useEffect(() => {
    if (lat != null && lng != null) map.setView([lat, lng], map.getZoom() < 13 ? 15 : map.getZoom());
  }, [lat, lng, map]);
  return null;
};

// Corrige el bug de Leaflet dentro de diálogos/modales: al montarse con
// tamaño 0, hay que recalcular el tamaño cuando el contenedor ya es visible.
const InvalidateSize = () => {
  const map = useMap();
  React.useEffect(() => {
    const fix = () => map.invalidateSize();
    const timers = [100, 350, 700, 1200].map((ms) => setTimeout(fix, ms));
    window.addEventListener('resize', fix);
    return () => { timers.forEach(clearTimeout); window.removeEventListener('resize', fix); };
  }, [map]);
  return null;
};

const CentroMapPicker = ({ lat, lng, radio, onChange }) => {
  const [buscar, setBuscar] = useState('');
  const [buscando, setBuscando] = useState(false);
  const hasPoint = lat != null && lng !== '' && lat !== '' && lng != null && !isNaN(lat) && !isNaN(lng);
  const center = hasPoint ? [Number(lat), Number(lng)] : DEFAULT_CENTER;

  const buscarTexto = async () => {
    if (!buscar.trim()) return;
    setBuscando(true);
    try {
      const r = await geocodificarDireccion(buscar);
      if (r) { onChange(r.lat, r.lng); toast.success('Ubicación encontrada'); }
      else toast.error('No se encontró esa dirección');
    } catch (e) {
      toast.error('No se pudo buscar la dirección');
    } finally { setBuscando(false); }
  };

  return (
    <div className="space-y-2" data-testid="centro-map-picker">
      <div className="flex gap-2">
        <Input value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Buscar dirección en el mapa…"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); buscarTexto(); } }}
          data-testid="centro-map-buscar" className="h-9" />
        <Button type="button" onClick={buscarTexto} disabled={buscando} data-testid="centro-map-buscar-btn"
          className="h-9 bg-zinc-900 hover:bg-zinc-800"><Search className="h-4 w-4" /></Button>
      </div>
      <div className="h-56 w-full rounded-lg overflow-hidden border border-zinc-200">
        <MapContainer center={center} zoom={hasPoint ? 15 : 5} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <InvalidateSize />
          <ClickHandler onPick={(la, ln) => onChange(la.toFixed(6), ln.toFixed(6))} />
          <Recenter lat={hasPoint ? Number(lat) : null} lng={hasPoint ? Number(lng) : null} />
          {hasPoint && (
            <>
              <Marker position={[Number(lat), Number(lng)]} />
              <Circle center={[Number(lat), Number(lng)]} radius={Number(radio) || 100}
                pathOptions={{ color: '#4f46e5', fillColor: '#4f46e5', fillOpacity: 0.12 }} />
            </>
          )}
        </MapContainer>
      </div>
      <p className="text-xs text-zinc-500">Pincha en el mapa o busca una dirección para fijar la ubicación. El círculo muestra el radio permitido.</p>
    </div>
  );
};

export default CentroMapPicker;
